import { useSearchParams } from 'react-router-dom';
import { queryKeyRecordId, useClientId } from './useClientId';
import { useMemo, useState } from 'react';
import lzs from 'lz-string';
import { getCurrentSearchParams } from '@/utils/functions';

export const queryKeyBuildings = 'buildings';
const separator = ';';
const enableCompression = true;
const maxUncompressedBuildings = 1000;
const compressionPrefix = 'comp-';

type SetBuildingIds = (buildingIds: string[], updateRecordId?: boolean) => void;

export function useBuildingIds(): [boolean, string[], SetBuildingIds] {
  const [clientId] = useClientId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAvailable, setIsAvailable] = useState(true);

  const spBuildingIds = searchParams.get(queryKeyBuildings);
  const buildingIds = useMemo(() => {
    if (!spBuildingIds) {
      return [];
    }

    const decompressedBuildingIds = decompress(spBuildingIds);
    return decompressedBuildingIds?.split(separator) ?? [];
  }, [spBuildingIds]);

  const setBuildingIds = (newBuildingIds: string[], updateRecordId = true) => {
    setIsAvailable(true);

    const searchParams = getCurrentSearchParams();

    if (newBuildingIds.length > 0) {
      searchParams.set(
        queryKeyBuildings,
        prepareBuildingIdsForQueryParams(newBuildingIds)
      );
    } else {
      searchParams.delete(queryKeyBuildings);
    }

    // support old approach for backward compatibility
    if (updateRecordId && clientId) {
      const recordId = newBuildingIds.length > 0 ? newBuildingIds[0] : clientId;
      searchParams.set(queryKeyRecordId, recordId);
    }

    setSearchParams(searchParams, { replace: false });
  };

  return [isAvailable, buildingIds, setBuildingIds];
}

function compress(ids: string[]): string {
  const input = ids.join(separator);

  if (enableCompression && ids.length > maxUncompressedBuildings) {
    return compressionPrefix + lzs.compressToEncodedURIComponent(input);
  }

  return input;
}

function decompress(input: string): string {
  if (input.startsWith(compressionPrefix)) {
    return lzs.decompressFromEncodedURIComponent(input.slice(compressionPrefix.length));
  }
  return input;
}

export function prepareBuildingIdsForQueryParams(buildingIds: string[]) {
  return compress(buildingIds);
}